import React, { ReactElement, useCallback, useMemo, useState } from "react";
import {
  Autocomplete,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Person } from "@mui/icons-material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import EditableCell from "../../../pages/AccommodationProviderPages/components/EditableCell";
import LargePopper from "../../../components/Table/assets/LargePopper";
import api from "../../../services/api";

type Currency = 1 | 2;

type RoomRow = {
  _key: string;
  id?: number;
  room_type_id?: number | null;
  room_type?: string;
  room_size?: number | string;
  quantity?: number | string;
  price_per_night?: number | string;
  price_per_person_per_night?: number | string;
  currency?: Currency;
};

export const ConfirmationTable = ({
                                    item,
                                    editableFields,
                                    onChange,
                                  }: {
  item: any;
  editableFields?: any;
  onChange?: (field: any, value: any) => void;
}): ReactElement => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<any>({});

  const initialRooms: RoomRow[] = useMemo(() => {
    const base = (item?.formatted?.rooms ?? []) as any[];

    return base.map((r, idx) => ({
      _key: r?._key ?? `${r?.id ?? "item"}-room-${idx}-${Date.now()}`,
      id: r?.id,
      room_type_id: r?.room_type_id ?? null,
      room_type: r?.room_type ?? "",
      room_size: r?.room_size ?? "",
      quantity: r?.quantity ?? "",
      price_per_night: r?.price_per_night ?? "",
      price_per_person_per_night: r?.price_per_person_per_night ?? "",
      currency: (Number(r?.currency) as Currency) || 1,
    }));
  }, [item?.formatted?.rooms, item?.id]);

  const [rooms, setRooms] = useState<RoomRow[]>(initialRooms);
  const [options, setOptions] = useState<any>([]);

  const toNumber = (value: any): number => {
    if (value === null || value === undefined || value === "") return 0;
    const normalized = String(value).replace(",", ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const round2 = (num: number): number => Number(num.toFixed(2));

  const handleFieldChange = (field: string, value: any) => {
    setFormValues((prev: any) => ({
      ...prev,
      [field]: value,
    }));

    onChange?.(field, value);
  };

  const fetchRoomTypes = useCallback(() => {
    api.get(`/accommodation-provider/room-types`).then((res: any) => {
      if (res?.data && res.data.length) {
        setOptions(res.data);
      }
    });
  }, []);

  const updateRooms = useCallback(
    (updater: (prev: RoomRow[]) => RoomRow[]) => {
      setRooms((prev) => {
        const next = updater(prev);
        onChange?.("rooms", next);
        return next;
      });
    },
    [onChange]
  );

  const addRoomRow = () => {
    updateRooms((prev) => [
      ...prev,
      {
        _key: `new-room-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        room_type_id: null,
        room_type: "",
        room_size: "",
        quantity: 1,
        price_per_night: "",
        price_per_person_per_night: "",
        currency: 1,
      },
    ]);
  };

  const removeRoomRow = (key: string) => {
    updateRooms((prev) => prev.filter((r) => r._key !== key));
  };

  const updateRoomField = (key: string, patch: Partial<RoomRow>) => {
    updateRooms((prev) =>
      prev.map((r) => (r._key === key ? { ...r, ...patch } : r))
    );
  };

  const updateRoomPriceField = (
    key: string,
    field: "price_per_night" | "price_per_person_per_night",
    value: any
  ) => {
    updateRooms((prev) =>
      prev.map((room) => {
        if (room._key !== key) return room;

        const roomSize = toNumber(item?.raw_formatted?.size);

        if (field === "price_per_person_per_night") {
          const perPersonNum = toNumber(value);

          return {
            ...room,
            price_per_person_per_night: value,
            price_per_night: roomSize > 0 ? round2(perPersonNum * roomSize) : "",
          };
        }

        const perNightNum = toNumber(value);

        return {
          ...room,
          price_per_night: value,
          price_per_person_per_night:
            roomSize > 0 ? round2(perNightNum / roomSize) : "",
        };
      })
    );
  };

  const updateRoomSizeField = (key: string, value: any) => {
    updateRooms((prev) =>
      prev.map((room) => {
        if (room._key !== key) return room;

        const nextSize = toNumber(value);
        const currentPerPerson = toNumber(room.price_per_person_per_night);

        return {
          ...room,
          room_size: value,
          price_per_night: nextSize > 0 ? round2(currentPerPerson * nextSize) : "",
        };
      })
    );
  };

  return (
    <>
      <Table sx={{ width: "100%" }} aria-label="simple table">
        <TableBody
          sx={{
            "& tr:last-child td, & tr:last-child th": { border: 0 },
            ".MuiTableCell-root": { padding: "2px" },
          }}
        >
          {item?.service_provider?.name && (
            <TableRow>
              <TableCell component="th" scope="row">
                Szolgáltató:
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold" }}
                dangerouslySetInnerHTML={{ __html: item?.service_provider?.name }}
              />
            </TableRow>
          )}

          <TableRow>
            <TableCell component="th" scope="row">
              Típus:
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold" }}
              dangerouslySetInnerHTML={{ __html: item?.formatted?.type }}
            />
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row">
              Csoport neve:
            </TableCell>
            <TableCell dangerouslySetInnerHTML={{ __html: item?.formatted?.group }} />
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row">
              Létszám:
            </TableCell>
            <TableCell dangerouslySetInnerHTML={{ __html: item?.formatted?.size }} />
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row">
              Érkezés:
            </TableCell>
            <TableCell dangerouslySetInnerHTML={{ __html: item?.formatted?.arrival }} />
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row">
              Távozás:
            </TableCell>
            <TableCell dangerouslySetInnerHTML={{ __html: item?.formatted?.departure }} />
          </TableRow>

          {item?.message_to_provider && (
            <TableRow>
              <TableCell component="th" scope="row">
                Travel City Üzenet:
              </TableCell>
              <TableCell>
                <Box sx={{ background: "#f6f6f6", p: "5px", borderRadius: "5px" }}>
                  {item?.message_to_provider}
                </Box>
              </TableCell>
            </TableRow>
          )}

          {item?.formatted?.custom_fields?.map((customField: any, idx: number) => (
            <TableRow key={`${customField?.column?.column_name ?? "cf"}-${idx}`}>
              <TableCell component="th" scope="row">
                {customField?.column?.name}:
              </TableCell>

              <EditableCell
                fieldKey={customField?.column?.column_name}
                value={
                  formValues[customField?.column?.column_name] ??
                  (customField?.value?.name ? customField?.value?.name : customField?.value)
                }
                editableFields={editableFields}
                editingField={editingField}
                setEditingField={setEditingField}
                onChange={(field, value) => handleFieldChange(field, value)}
                row={item}
                type={customField?.column?.type}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ mt: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="body2">Szobák:</Typography>

          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={addRoomRow}
          >
            Szoba hozzáadása
          </Button>
        </Stack>

        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          {rooms.map((room) => (
            <ListItem
              key={room._key}
              sx={{
                background: "#f6f6f6",
                borderRadius: "5px",
                mb: 0.5,
              }}
              secondaryAction={
                <Tooltip title="Sor törlése">
                  <IconButton edge="end" color="error" onClick={() => removeRoomRow(room._key)}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText
                primary={
                  <Stack gap={"6px"} direction={"row"} alignItems="flex-start">
                    <Autocomplete
                      sx={{ background: "#fefefe" }}
                      value={
                        room.room_type
                          ? { id: room.room_type_id, name: room.room_type }
                          : null
                      }
                      freeSolo
                      size="small"
                      fullWidth
                      PopperComponent={LargePopper}
                      onOpen={fetchRoomTypes}
                      getOptionLabel={(option: any) =>
                        typeof option === "string" ? option : option?.name ?? ""
                      }
                      isOptionEqualToValue={(opt: any, val: any) => opt?.id === val?.id}
                      options={options}
                      renderInput={(params) => <TextField {...params} label="Szoba típusa" />}
                      onChange={(event, newValue) => {
                        if (!newValue) {
                          updateRoomField(room._key, { room_type_id: null, room_type: "" });
                          return;
                        }

                        if (typeof newValue === "string") {
                          updateRoomField(room._key, { room_type_id: null, room_type: newValue });
                          return;
                        }

                        updateRoomField(room._key, {
                          room_type_id: newValue?.id ?? null,
                          room_type: newValue?.name ?? "",
                        });
                      }}
                      onInputChange={(event, inputValue, reason) => {
                        if (reason === "input") {
                          updateRoomField(room._key, {
                            room_type_id: null,
                            room_type: inputValue,
                          });
                        }
                      }}
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel htmlFor={`room-size-${room._key}`}>Férőhely</InputLabel>
                      <OutlinedInput
                        sx={{ background: "#fefefe" }}
                        id={`room-size-${room._key}`}
                        label="Férőhely"
                        value={room.room_size}
                        onChange={(e) => updateRoomSizeField(room._key, e.target.value)}
                        endAdornment={
                          <InputAdornment position="end">
                            <Person />
                          </InputAdornment>
                        }
                      />
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel htmlFor={`room-qty-${room._key}`}>Darabszám</InputLabel>
                      <OutlinedInput
                        sx={{ background: "#fefefe" }}
                        id={`room-qty-${room._key}`}
                        label="Darabszám"
                        value={room.quantity}
                        onChange={(e) => updateRoomField(room._key, { quantity: e.target.value })}
                      />
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel htmlFor={`room-price-${room._key}`}>Ár / Éj</InputLabel>
                      <OutlinedInput
                        id={`room-price-${room._key}`}
                        label="Ár / Éj"
                        value={room.price_per_night}
                        onChange={(e) =>
                          updateRoomPriceField(room._key, "price_per_night", e.target.value)
                        }
                        sx={{ background: "#fefefe" }}
                        endAdornment={
                          <InputAdornment position="end">
                            <Select
                              value={room.currency ?? 1}
                              onChange={(e) =>
                                updateRoomField(room._key, {
                                  currency: Number(e.target.value) as Currency,
                                })
                              }
                              variant="standard"
                              disableUnderline
                              sx={{ minWidth: 40, ml: 1, fontSize: "0.9rem" }}
                            >
                              <MenuItem value={1}>€</MenuItem>
                              <MenuItem value={2}>Ft</MenuItem>
                            </Select>
                          </InputAdornment>
                        }
                      />
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel htmlFor={`room-price-per-person-${room._key}`}>
                        Ár / Éj / fő
                      </InputLabel>
                      <OutlinedInput
                        id={`room-price-per-person-${room._key}`}
                        label="Ár / Éj / fő"
                        value={room.price_per_person_per_night}
                        onChange={(e) =>
                          updateRoomPriceField(
                            room._key,
                            "price_per_person_per_night",
                            e.target.value
                          )
                        }
                        sx={{ background: "#fefefe" }}
                        endAdornment={
                          <InputAdornment position="end">
                            <Select
                              value={room.currency ?? 1}
                              onChange={(e) =>
                                updateRoomField(room._key, {
                                  currency: Number(e.target.value) as Currency,
                                })
                              }
                              variant="standard"
                              disableUnderline
                              sx={{ minWidth: 40, ml: 1, fontSize: "0.9rem" }}
                            >
                              <MenuItem value={1}>€</MenuItem>
                              <MenuItem value={2}>Ft</MenuItem>
                            </Select>
                          </InputAdornment>
                        }
                      />
                    </FormControl>
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  );
};